import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Greeting, StatCard, Panel, EmptyRow } from "@/components/shared/DashboardPrimitives";
import { Icon } from "@/lib/icons";
import NeedsAttention from "@/components/NeedsAttention";
import GardenSnapshot from "@/components/GardenSnapshot";
import MyMessages from "@/components/MyMessages";
import WaysToGive from "@/components/giving/WaysToGive";

// One dashboard for EVERY admin-layout role. It assembles itself purely from the
// combined permission set the backend already resolves (me.nav / me.permissions —
// union across all of a person's roles, higher level wins). Nothing here keys off a
// role name, so any role — one of the originals or one created a minute ago — lands
// on a real, working dashboard built from its own grants.

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const dollars = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDay = (s) => (s ? new Date(s).toLocaleDateString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric" }) : "");
const fmtMeeting = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric" }) : "");
const fmtWhen = (s) => (s ? new Date(s).toLocaleDateString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const GROUP_LABELS = {
  membership: "Membership", gardens: "Gardens", events: "Events",
  fundraising: "Fundraising & Sales", reimbursements: "Reimbursements & In-Kind",
  communications: "Communications", governance: "Governance", settings: "Settings",
};
const GROUP_ORDER = ["membership", "gardens", "events", "fundraising", "reimbursements", "communications", "governance", "settings", "other"];
const LEVEL_STYLE = {
  view: "bg-ink/10 text-ink/70", edit: "bg-gold/30 text-ink", full: "bg-olive/25 text-dark-olive",
};

function Metric({ label, value, onClick, danger }) {
  return (
    <button onClick={onClick} className="w-full text-left px-4 py-2.5 border-b border-ink/8 last:border-0 flex items-center justify-between hover:bg-warm-cream/60">
      <span className="text-[14px] text-ink/75">{label}</span>
      <span className={`text-[15px] font-zilla font-semibold ${danger ? "text-maroon" : ""}`}>{value}</span>
    </button>
  );
}

function ModulePanel({ title, to, onNavigate, testid, children }) {
  return (
    <Panel title={<button onClick={() => onNavigate && onNavigate(to)} className="hover:text-maroon transition-colors">{title}</button>}>
      <div data-testid={testid}>{children}</div>
    </Panel>
  );
}

export default function DynamicDashboard({ me, onNavigate }) {
  const [sd, setSd] = useState(null);
  const [sum, setSum] = useState(null);
  const [don, setDon] = useState(null);
  useEffect(() => { api.get("/dashboard/super").then((r) => setSd(r.data)).catch(() => setSd(null)); }, []);
  useEffect(() => { api.get("/dashboard/summary").then((r) => setSum(r.data)).catch(() => setSum(null)); }, []);
  useEffect(() => { api.get("/donations/stats").then((r) => setDon(r.data)).catch(() => setDon(null)); }, []);

  const go = (to) => () => onNavigate && onNavigate(to);
  // Navigate AND hand the target page a one-shot filter (same sessionStorage pattern
  // used for opening a specific event/member), so the tile's number equals the rows shown.
  const goFiltered = (to, key, val) => () => { try { sessionStorage.setItem(key, val); } catch { /* ignore */ } onNavigate && onNavigate(to); };
  const navItems = (me.nav || []).filter((n) => n.id !== "dashboard");
  const navIds = new Set(navItems.map((n) => n.id));
  const can = (id) => navIds.has(id);

  const m = sd?.membership || {}; const vi = sd?.visitors || {}; const ga = sd?.gardens || {};
  const ev = sd?.events || {}; const bk = sd?.bricks || {}; const gr = sd?.grants || {};
  const gov = sd?.governance || {}; const ops = sd?.ops || {}; const sec = sd?.security || {};
  const v = (x) => (sd ? x ?? 0 : "…");

  // Org stat tiles — each shown only if the person can reach its target section.
  const allTiles = [
    { id: "signups", label: "New signups (7d)", value: v(m.new_signups_week), note: "Members added this week", color: "#808c20", to: "membership_directory" },
    { id: "active", label: "Active members", value: v(m.active), note: "Currently active", color: "#435912", to: "membership_directory" },
    { id: "lapsed", label: "Lapsed", value: v(m.lapsed), note: "Dues past renewal", color: "#daa405", to: "membership_directory" },
    { id: "suspended", label: "Suspended", value: v(m.suspended), note: `${v(m.suspended_with_msg)} with a waiting message`, color: "#800000", to: "membership_directory" },
    { id: "fulfillment", label: "To fulfill", value: v(ops.fulfillment_pending), note: "Items to print / mail", color: "#800000", to: "fulfillment" },
    { id: "reimb", label: "Reimbursements", value: v(ops.reimb_open), note: "Waiting for review", color: "#800000", to: "reimbursements_queue" },
    { id: "grants", label: "Open grants", value: v(gr.open), note: `${sd ? dollars(gr.requested) : "…"} requested`, color: "#14140f", to: "grants" },
    { id: "votes", label: "Open board votes", value: v(gov.open_votes), note: `${v(gov.open_tasks)} open tasks`, color: "#435912", to: "votes_approvals" },
    { id: "bricks", label: "Brick $ raised", value: sd ? money(bk.dollars_cents) : "…", note: `${v(bk.sold)} bricks sold`, color: "#daa405", to: "legacy_bricks" },
    { id: "donations", label: "Raised this month", value: don ? money(don.month_cents) : "…", note: `${don ? money(don.year_cents) : "…"} this year`, color: "#435912", to: "donations" },
  ];
  const tiles = sd ? allTiles.filter((t) => can(t.to)) : [];

  const attention = sd ? [
    m.suspended_with_msg > 0 && can("membership_directory") && { label: "Suspended members with a waiting message", count: m.suspended_with_msg, to: "membership_directory", danger: true },
    gov.minutes_pending > 0 && can("votes_approvals") && { label: "Minutes waiting on approval", count: gov.minutes_pending, to: "votes_approvals", danger: true },
    ops.reimb_open > 0 && can("reimbursements_queue") && { label: "Reimbursements waiting for review", count: ops.reimb_open, to: "reimbursements_queue" },
    ops.fulfillment_pending > 0 && can("fulfillment") && { label: "Items waiting to print or mail", count: ops.fulfillment_pending, to: "fulfillment" },
    bk.missing_info > 0 && can("legacy_bricks") && { label: "Brick orders missing information", count: bk.missing_info, to: "legacy_bricks", danger: true },
    gov.open_votes > 0 && can("votes_approvals") && { label: "Open board votes", count: gov.open_votes, to: "votes_approvals" },
    m.in_grace > 0 && can("membership_directory") && { label: "Members mid dues-grace period", count: m.in_grace, to: "membership_directory" },
    vi.repeat_no_join > 0 && can("visitors") && { label: "Repeat visitors who haven't joined", count: vi.repeat_no_join, to: "visitors", filterKey: "rca_visitors_filter", filterVal: "repeat_no_join" },
    sec.super_admins === 1 && can("roles_access") && { label: "Only ONE Super Admin account exists", count: "!", to: "roles_access", danger: true },
  ].filter(Boolean) : [];

  // Metric panels, keyed by the section they belong to (shown only with access + data).
  const panels = [];
  if (sd && can("membership_directory")) panels.push(
    <ModulePanel key="membership" title="Membership" to="membership_directory" onNavigate={onNavigate} testid="mod-membership">
      <Metric label="Active members (people)" value={v(m.active)} onClick={go("membership_directory")} />
      <Metric label="Active memberships" value={v(m.active_memberships)} onClick={go("membership_directory")} />
      <Metric label="New this week" value={v(m.new_signups_week)} onClick={go("membership_directory")} />
      <Metric label="Lapsed" value={v(m.lapsed)} onClick={go("membership_directory")} danger={m.lapsed > 0} />
      <Metric label="In grace period" value={v(m.in_grace)} onClick={go("membership_directory")} />
      <Metric label="Suspended (with message)" value={`${v(m.suspended)} (${v(m.suspended_with_msg)})`} onClick={go("membership_directory")} danger={m.suspended_with_msg > 0} />
    </ModulePanel>);
  if (sd && can("visitors")) panels.push(
    <ModulePanel key="visitors" title="Visitors" to="visitors" onNavigate={onNavigate} testid="mod-visitors">
      <Metric label="New this month" value={v(vi.new_month)} onClick={go("visitors")} />
      <Metric label="Repeat, not joined (2+ visits)" value={v(vi.repeat_no_join)} onClick={goFiltered("visitors", "rca_visitors_filter", "repeat_no_join")} />
      <Metric label="Invited to join" value={v(vi.invited)} onClick={go("visitors")} />
      <Metric label="Invited → joined" value={sd ? `${vi.joined} (${vi.conversion_pct}%)` : "…"} onClick={go("visitors")} />
    </ModulePanel>);
  if (sd && can("garden_plots")) panels.push(
    <ModulePanel key="gardens" title="Gardens" to="garden_plots" onNavigate={onNavigate} testid="mod-gardens">
      <Metric label="Total gardeners" value={v(ga.gardeners)} onClick={go("garden_plots")} />
      <Metric label="Plots assigned / total" value={sd ? `${ga.plots_assigned} / ${ga.plots_total}` : "…"} onClick={go("garden_plots")} />
      <Metric label="Open needs (all plots)" value={v(ga.open_needs)} onClick={go("garden_plots")} danger={ga.open_needs > 0} />
      <Metric label="Hours logged this month" value={v(ga.hours_month)} onClick={go("garden_plots")} />
    </ModulePanel>);
  if (sd && can("legacy_bricks")) panels.push(
    <ModulePanel key="bricks" title="Legacy Bricks" to="legacy_bricks" onNavigate={onNavigate} testid="mod-bricks">
      <Metric label="Bricks sold" value={v(bk.sold)} onClick={go("legacy_bricks")} />
      <Metric label="Dollars raised" value={sd ? money(bk.dollars_cents) : "…"} onClick={go("legacy_bricks")} />
      <Metric label="Orders needing attention" value={v(bk.need_attention)} onClick={goFiltered("legacy_bricks", "rca_bricks_filter", "attention")} danger={bk.need_attention > 0} />
      <Metric label="Missing information" value={v(bk.missing_info)} onClick={go("legacy_bricks")} danger={bk.missing_info > 0} />
    </ModulePanel>);
  if (can("donations")) panels.push(
    <ModulePanel key="donations" title="Donations" to="donations" onNavigate={onNavigate} testid="mod-donations">
      <Metric label="Raised this year" value={don ? money(don.year_cents) : "…"} onClick={go("donations")} />
      <Metric label="Raised this month" value={don ? money(don.month_cents) : "…"} onClick={go("donations")} />
      {(don?.by_fund || []).slice(0, 5).map((fnd, i) => (
        <Metric key={i} label={fnd.title} value={`${money(fnd.cents)} · ${fnd.count} gift${fnd.count === 1 ? "" : "s"}`} onClick={go("donations")} />
      ))}
      {don && (don.by_fund || []).length === 0 && <div className="px-4 py-2 text-[13px] text-ink/55">No paid gifts yet.</div>}
    </ModulePanel>);
  if (sd && can("grants")) panels.push(
    <ModulePanel key="grants" title="Grants" to="grants" onNavigate={onNavigate} testid="mod-grants">
      <Metric label="Open grants" value={v(gr.open)} onClick={go("grants")} />
      <Metric label="Total requested" value={sd ? dollars(gr.requested) : "…"} onClick={go("grants")} />
      <Metric label="Total awarded" value={sd ? dollars(gr.awarded) : "…"} onClick={go("grants")} />
    </ModulePanel>);
  if (sd && (can("board_meetings") || can("votes_approvals") || can("my_tasks"))) panels.push(
    <ModulePanel key="gov" title="Board Governance" to="board_meetings" onNavigate={onNavigate} testid="mod-governance">
      <Metric label="Next meeting" value={gov.next_meeting ? fmtMeeting(gov.next_meeting.date) : "None set"} onClick={go("board_meetings")} />
      <Metric label="Open votes" value={v(gov.open_votes)} onClick={go("votes_approvals")} danger={gov.open_votes > 0} />
      <Metric label="Open board tasks" value={v(gov.open_tasks)} onClick={go("my_tasks")} />
      <Metric label="Minutes waiting approval" value={v(gov.minutes_pending)} onClick={go("votes_approvals")} danger={gov.minutes_pending > 0} />
    </ModulePanel>);
  if (sd && (can("reimbursements_queue") || can("fulfillment") || can("targeted_messages"))) panels.push(
    <ModulePanel key="ops" title="Operations" to={can("reimbursements_queue") ? "reimbursements_queue" : "fulfillment"} onNavigate={onNavigate} testid="mod-ops">
      {can("reimbursements_queue") && <Metric label="Reimbursements to review" value={v(ops.reimb_open)} onClick={go("reimbursements_queue")} danger={ops.reimb_open > 0} />}
      {can("reimbursements_queue") && <Metric label="In-kind to review" value={v(ops.inkind_open)} onClick={go("reimbursements_queue")} danger={ops.inkind_open > 0} />}
      {can("fulfillment") && <Metric label="Items to fulfill" value={v(ops.fulfillment_pending)} onClick={go("fulfillment")} danger={ops.fulfillment_pending > 0} />}
      {can("targeted_messages") && <Metric label="Messages sent (30d)" value={v(ops.messages_30)} onClick={go("targeted_messages")} />}
    </ModulePanel>);
  if (sd && can("roles_access")) panels.push(
    <ModulePanel key="security" title="Roles & security" to="roles_access" onNavigate={onNavigate} testid="mod-security">
      <Metric label="Super Admin accounts" value={v(sec.super_admins)} onClick={go("roles_access")} danger={sec.super_admins === 1} />
      <div className="px-4 pt-2 pb-1 text-[12px] uppercase tracking-wide text-ink/45 font-bold">Recent security activity</div>
      {sd && (sec.audit || []).length === 0 && <div className="px-4 py-2 text-[13px] text-ink/55">No recent role or account changes.</div>}
      {(sec.audit || []).map((a, i) => (
        <div key={i} className="px-4 py-2 border-b border-ink/8 last:border-0">
          <div className="text-[14px] font-medium">{a.action?.replace(/[._]/g, " ")}{a.label ? ` — ${a.label}` : ""}</div>
          <div className="text-[12px] text-ink/50">{a.actor ? `${a.actor} · ` : ""}{fmtWhen(a.at)}</div>
        </div>
      ))}
    </ModulePanel>);
  if (sd && can("events")) panels.push(
    <ModulePanel key="events" title="Events & check-in" to="events" onNavigate={onNavigate} testid="mod-events">
      {ev.live_session?.active && <div className="px-4 py-2 text-[13px] font-semibold text-maroon">● Check-in live{ev.live_session.event_title ? ` — ${ev.live_session.event_title}` : ""}</div>}
      {(ev.upcoming || []).slice(0, 4).map((e) => (
        <button key={e.event_id} data-testid={`dash-event-${e.event_id}`} onClick={() => { sessionStorage.setItem("rca_open_event", e.event_id); onNavigate && onNavigate("events"); }} className="w-full text-left px-4 py-2.5 border-b border-ink/8 last:border-0 flex items-center justify-between hover:bg-warm-cream/60">
          <div><div className="text-[14px] font-medium">{e.title}</div><div className="text-[12px] text-ink/55">{fmtDay(e.start)}</div></div>
          <span className="text-[13px] font-semibold text-dark-olive">{e.going} going</span>
        </button>
      ))}
      {sd && (ev.upcoming || []).length === 0 && <EmptyRow>No upcoming events.</EmptyRow>}
    </ModulePanel>);

  // Grouped card grid over EVERY accessible section — guarantees nothing granted is missed.
  const grouped = {};
  navItems.forEach((n) => {
    const g = n.group && GROUP_LABELS[n.group] ? n.group : "other";
    (grouped[g] = grouped[g] || []).push(n);
  });
  const groupKeys = GROUP_ORDER.filter((g) => grouped[g]?.length);

  return (
    <div data-testid="dynamic-dashboard">
      <Greeting me={me} subtitle={me.is_super_admin ? "The whole organization at a glance." : `Your ${me.role.name} workspace — everything your access reaches.`} />

      {sd && ev.live_session?.active && can("checkin") && (
        <button onClick={go("checkin")} data-testid="live-checkin-banner" className="w-full text-left rounded-xl p-4 bg-maroon text-cream mb-6 flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full bg-cream animate-pulse" />
          <span className="font-semibold">Check-in is LIVE{ev.live_session.event_title ? ` — ${ev.live_session.event_title}` : " (general)"}</span>
          <span className="ml-auto text-cream/80 text-[14px]">Open station →</span>
        </button>
      )}

      {tiles.length > 0 && (
        <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))" }} data-testid="dash-tiles">
          {tiles.map((t) => <StatCard key={t.id} {...t} onClick={go(t.to)} data-testid={`stat-${t.id}`} />)}
        </div>
      )}

      <div className="grid gap-4 items-start mb-4 grid-cols-1 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <Panel title="Needs your attention">
          <div className="p-2" data-testid="system-attention">
            {sd && attention.length === 0 && <EmptyRow>Nothing needs a look right now.</EmptyRow>}
            {!sd && <EmptyRow>Your section snapshots appear here.</EmptyRow>}
            {attention.map((a, i) => (
              <button key={i} data-testid={`attn-${a.to}-${i}`} onClick={a.filterKey ? goFiltered(a.to, a.filterKey, a.filterVal) : go(a.to)} className="w-full text-left px-4 py-3 border-b border-ink/8 last:border-0 flex items-center justify-between hover:bg-warm-cream/60">
                <span className={`text-[15px] ${a.danger ? "text-maroon font-semibold" : "font-medium"}`}>{a.label}</span>
                <span className={`shrink-0 ml-3 text-[13px] font-bold px-2.5 py-1 rounded-full ${a.danger ? "bg-maroon text-cream" : "bg-gold/30 text-ink"}`}>{a.count}</span>
              </button>
            ))}
          </div>
        </Panel>
        <div className="flex flex-col gap-4">
          <NeedsAttention onNavigate={onNavigate} />
          <WaysToGive prefill={{ name: me.user.name, email: me.user.email }} onSeeAll={() => onNavigate && onNavigate("mm_give")} />
          {can("garden_plots") && <GardenSnapshot onNavigate={onNavigate} />}
          {sum && can("reports") && (
            <div className="bg-ink text-cream rounded-xl p-5" data-testid="grant-ready-figure">
              <div className="text-[13px] uppercase tracking-widest font-bold text-gold">Grant-ready figure</div>
              <div className="font-zilla text-5xl font-semibold leading-none my-1.5">{sum.persons_served ?? 0}</div>
              <div className="text-[15px] text-cream/80 leading-snug">Unduplicated persons served (headcount), drawn from real check-in records.</div>
              <div className="text-[14px] text-cream/70 mt-1" data-testid="memberships-served">{sum.memberships_served ?? 0} memberships attended</div>
            </div>
          )}
        </div>
      </div>

      {panels.length > 0 && (
        <div className="grid gap-4 items-start mb-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))" }}>
          {panels}
        </div>
      )}

      {/* Section overview with access levels — SUPER ADMIN ONLY. Everyone else navigates
          via the single sidebar menu and never sees access-level info. */}
      {me.is_super_admin && groupKeys.map((g) => (
        <div key={g} className="mb-6" data-testid={`dash-group-${g}`}>
          <h2 className="font-zilla text-xl font-semibold mb-2">{GROUP_LABELS[g] || "More"}</h2>
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 200px), 1fr))" }}>
            {grouped[g].map((n) => (
              <button key={n.id} data-testid={`dash-section-${n.id}`} onClick={go(n.id)}
                className="bg-cream border border-ink/15 rounded-xl p-4 text-left hover:border-maroon/40 hover:shadow-sm transition-all flex items-start gap-3">
                <Icon name={n.icon} className="w-6 h-6 text-maroon shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold leading-tight">{n.label}</div>
                  <span className={`inline-block mt-1.5 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${LEVEL_STYLE[n.level] || "bg-ink/10 text-ink/70"}`} data-testid={`dash-level-${n.id}`}>{n.level}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-2"><MyMessages /></div>
    </div>
  );
}
